import { useLayoutEffect, type RefObject } from "react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

type ParallaxTarget = HTMLElement;

interface UseGsapScrollParallaxOptions {
	y?: number;
	opacity?: number;
	scale?: number;
	start?: string;
	end?: string;
}

export function useGsapScrollParallax<T extends ParallaxTarget>(
	ref: RefObject<T | null>,
	options: UseGsapScrollParallaxOptions = {},
) {
	const {
		y = -120,
		opacity = 0.85,
		scale = 0.96,
		start = "top top",
		end = "bottom top",
	} = options;

	useLayoutEffect(() => {
		const element = ref.current;

		if (!element) {
			return;
		}

		if (
			typeof window !== "undefined" &&
			window.matchMedia("(prefers-reduced-motion: reduce)").matches
		) {
			return;
		}

		const ctx = gsap.context(() => {
			gsap.fromTo(
				element,
				{ y: 0 },
				{
					y,
					ease: "none",
					scrollTrigger: {
						trigger: element,
						start,
						end,
						scrub: true,
					},
				},
			);

			gsap.fromTo(
				element,
				{ opacity: 1, scale: 1 },
				{
					opacity,
					scale,
					ease: "none",
					scrollTrigger: {
						trigger: element,
						start,
						end,
						scrub: true,
					},
				},
			);
		}, element);

		return () => ctx.revert();
	}, [end, opacity, ref, scale, start, y]);
}
